/**
 * Workflows in words (D401), for the Workflows tab, a workflow's page and a run's page: what a version does, who or what
 * started a run, how long it took, and its state as a dot and a word. The contract's words (STATE_WORDS, TRIGGER_WORDS,
 * ACTION_CLASS_WORDS) wherever they fit; never "DAG", "node" for a step, "edge" or "orchestration".
 */
import { ACTION_CLASS_WORDS, STATE_WORDS, TRIGGER_WORDS, type WorkflowManifest, type WorkflowRun, type WorkflowRunView, type WorkflowState } from '@aioe/shared';

import { STATE_MARK } from './workflowPicture.ts';

const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "3 steps · 1 needs a person’s approval · at most high impact · uses SOC_KEY". */
export function workflowWhat(m: Pick<WorkflowManifest, 'steps' | 'highest' | 'secrets' | 'scripts'>): string {
  const total = m.steps.agent + m.steps.code + m.steps.approval;
  const parts = [count(total, 'step'), m.steps.approval ? `${m.steps.approval} ${m.steps.approval === 1 ? 'needs' : 'need'} a person’s approval` : 'no approvals', `at most ${ACTION_CLASS_WORDS[m.highest].toLowerCase()}`];
  if (m.secrets.length) parts.push(`uses ${m.secrets.join(', ')}`);
  if (m.scripts) parts.push('runs a script from the repository');
  return parts.join(' · ');
}

/** Who or what started it: the person by name, else the trigger in words. */
export function startedWords(run: Pick<WorkflowRun, 'trigger'>): string {
  const by = run.trigger.by?.name;
  if (by) return run.trigger.kind === 'organisation' ? `Started here by ${by}` : `Started by ${by}`;
  if (run.trigger.kind === 'event' || (run.trigger.kind === 'organisation' && run.trigger.event)) return run.trigger.event ? `Started when ${run.trigger.event.summary}` : TRIGGER_WORDS.event;
  return TRIGGER_WORDS[run.trigger.kind];
}

/** "Took 4 min", "Running for 2 h", or nothing when it never started. */
export function durationWords(ms: number | undefined, state: WorkflowState | WorkflowRun['state']): string {
  if (ms === undefined) return '';
  const minutes = Math.round(ms / 60_000);
  const hours = ms / 3_600_000;
  const words = ms < 59_500 ? `${Math.max(1, Math.round(ms / 1000))} s` : minutes < 60 ? `${minutes} min` : hours < 23.95 ? `${hours < 9.95 ? Number(hours.toFixed(1)) : Math.round(hours)} h` : `${Number((ms / 86_400_000).toFixed(1))} days`;
  return state === 'running' || state === 'queued' || state === 'waiting' ? `Running for ${words}` : `Took ${words}`;
}

const waitingNow = (v: Pick<WorkflowRunView, 'waitingFor' | 'run'>) => v.run.state === 'running' && v.waitingFor.length > 0;

/** A run's dot: waiting for a person shows as the one amber thing. */
export function runStateMark(v: Pick<WorkflowRunView, 'waitingFor' | 'run'>): (typeof STATE_MARK)[WorkflowState] {
  return waitingNow(v) ? 'medium' : STATE_MARK[v.run.state];
}

/** A run's state in words: "Waiting for a person" from what holds it, else the contract's word. */
export function runStateWords(v: Pick<WorkflowRunView, 'waitingFor' | 'run' | 'reported'>): string {
  if (waitingNow(v)) return 'Waiting for a person';
  if (!v.reported && v.run.state === 'queued') return 'Asked to start';
  return STATE_WORDS[v.run.state];
}
